import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import { MealPatternNameKind } from "@/gql/graphql";

import { Column } from "./Column";
import MealPatternItem from "./MealPatternItem";
import MealPlanFormStore from "./MealPlanFormStore";
import { PlannedMeal } from "./PlannedMeal";
import { PlannedMealItem } from "./PlannedMealItem";

export class Cell {
  column: Column;
  plannedMeal: PlannedMeal;
  plannedMealItems: PlannedMealItem[] = [];
  mealPatternItems: MealPatternItem[] = [];

  isDeleting = false;

  private store: MealPlanFormStore;

  get mode(): "meal_pattern_item" | "planned_meal_item" {
    return this.column.mode === "meal_pattern"
      ? "meal_pattern_item"
      : "planned_meal_item";
  }

  get items(): PlannedMealItem[] | MealPatternItem[] {
    return this.mode === "meal_pattern_item"
      ? this.mealPatternItems
      : this.plannedMealItems;
  }

  get key(): string {
    return [this.column.index, this.plannedMeal.uuid].join("_");
  }

  get label(): string {
    return [this.plannedMeal.label, this.column.label].join(" ");
  }

  get totalEnergyKcal(): number {
    return (this.items as Array<PlannedMealItem | MealPatternItem>).reduce(
      (total, item) => total + item.energyKcal,
      0
    );
  }

  get isActive(): boolean {
    return this.store.cells.active === this;
  }

  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  constructor(
    store: MealPlanFormStore,
    plannedMealRow: PlannedMeal,
    dayColumn: Column
  ) {
    makeAutoObservable(this);
    this.store = store;
    this.plannedMeal = plannedMealRow;
    this.column = dayColumn;
  }

  addPlannedMealItem(
    food: PlannedMealItem["food"],
    foodPortion: PlannedMealItem["foodPortion"],
    amount: number,
    uuid = uuidv4()
  ): void {
    const existing = this.plannedMealItems.find((i) => i.food.id === food.id);
    // If food item is already added, just update the selected food portion and
    // amount.
    if (existing) {
      existing.foodPortion = foodPortion;
      existing.amount = amount;
    } else {
      this.plannedMealItems.push(
        new PlannedMealItem(this.store, this, food, foodPortion, amount, uuid)
      );
    }
  }

  addMealPatternItem(
    name: MealPatternNameKind,
    amountServings: number,
    food: MealPatternItem["food"] | undefined | null,
    foodPortion: MealPatternItem["foodPortion"],
    uuid = uuidv4()
  ): void {
    const existing = this.mealPatternItems.find((i) => i.name === name);
    if (existing) {
      existing.amountServings = amountServings;
    } else {
      this.mealPatternItems.push(
        new MealPatternItem(this, name, amountServings, food, foodPortion, uuid)
      );
    }
  }

  removePlannedMealItem(item: PlannedMealItem): void {
    const index = this.plannedMealItems.indexOf(item);
    if (index !== -1) this.plannedMealItems.splice(index, 1);
    if (this.isEmpty) this.isDeleting = false;
  }

  removeMealPatternItem(item: MealPatternItem): void {
    const index = this.mealPatternItems.indexOf(item);
    if (index !== -1) this.mealPatternItems.splice(index, 1);
    if (this.isEmpty) this.isDeleting = false;
  }

  copyFromCell(cell: Cell): void {
    // Do nothing if they're not in the same mode.
    if (cell.mode !== this.mode) return;

    if (cell.mode === "planned_meal_item") {
      this.plannedMealItems = cell.plannedMealItems.map(
        (i) =>
          new PlannedMealItem(this.store, this, i.food, i.foodPortion, i.amount)
      );
    } else {
      this.mealPatternItems = cell.mealPatternItems.map(
        (i) =>
          new MealPatternItem(
            this,
            i.name,
            i.amountServings,
            i.food,
            i.foodPortion
          )
      );
    }
  }

  replacePlannedMealItems(newItems: PlannedMealItem[]): void {
    this.plannedMealItems = newItems.map(
      (i) =>
        new PlannedMealItem(this.store, this, i.food, i.foodPortion, i.amount)
    );
  }

  clearItems(): void {
    this.plannedMealItems = [];
    this.mealPatternItems = [];
  }

  toggleDeleting(): void {
    this.isDeleting = !this.isDeleting;
  }
}
